import React, { Fragment, memo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { getDisplaySqft } from 'Utils/listingUtils'
import { formatPrice } from 'Utils/inputUtils'
import EllipsisText from 'Components/EllipsisText'

import {
  _ListingSpecUl,
  _BeforeSeparator,
  _InfoContainer,
  _MaintFee,
  _BrokerageInfo,
  _Separator,
} from './styles'

const ListingSpec = (
  {
    className = '',
    listing,
    offer,
    showPsf = false,
    userIsAuthentificated,
    // hide sqftRange on samller than 320 device
    smallerThan320 = false,
    // even sqft need to go second line from with 375
    smallerThan375 = false,
    isBc,
  }) => {
  if(!listing) return null
  const secondaryInfoRef = useRef(null)
  const listRef = useRef(null)
  // when the anything on the secondInfo span goes to second line
  // making sure not to show separator
  useEffect(() => {
    const span = secondaryInfoRef.current.querySelector('span')
    const list = listRef.current && listRef.current.offsetHeight
    if(span && list !== span.offsetHeight) {
      span.classList.remove('spanBeforeSeparator')
      secondaryInfoRef.current.style.width = '100%'
    }
  }, [smallerThan320, smallerThan375, userIsAuthentificated])

  const beds = listing.bed_type
  const baths = listing.bathrooms
  const sqftMax = listing.sqft_max
  const sqftMin = listing.sqft_min
  const parkings = listing.parking_spots
  // need to hide sqft if user is not authenticated
  const sqft = (userIsAuthentificated && listing.sqft) || null
  let psf = 0
  if(sqft) {
    psf = parseFloat(listing.closed_price || listing.asking_price) / sqft
    psf = offer === 'Sale' ? Math.round(psf) : psf.toFixed(2)
  }
  let sqftRange = getDisplaySqft(listing.sqft, sqftMin, sqftMax, userIsAuthentificated, listing.sqft_source)
  sqftRange = sqftRange && sqftRange + ' sqft'
  const hasSqftRange = !smallerThan320 && sqftRange && sqftRange.match('-')
  const showSqftNextLine = smallerThan375 && sqft

  const hasMaint = offer === 'Sale' && listing.maintenance_fee && Number(listing.maintenance_fee) > 0
  return (
    <_ListingSpecUl ref={listRef} className={className}>
      <_InfoContainer>
        {
          listing.bed_type ? (
            <Fragment>
              <span data-test-id={'beds'}>{beds}{beds === 'Studio' ? '' : 'BD'}</span>
              <_BeforeSeparator>{baths}BA</_BeforeSeparator>
              <_BeforeSeparator>
                {parkings || 0} Parking
              </_BeforeSeparator>
              {
                sqft && !smallerThan375 && (
                  <_BeforeSeparator data-test-id={'sqft'} bold>
                    {sqft} sqft
                  </_BeforeSeparator>
                )
              }
            </Fragment>
          ) : <span data-test-id={'parkingSpace'}>{listing.property_type || 'Parking Space'}</span>
        }
      </_InfoContainer>
      <div
        data-test-id={'secondaryInfo'}
        ref={secondaryInfoRef}
      >
        <_InfoContainer isBc={isBc}>
          {
            showSqftNextLine ? (
              <_BeforeSeparator
                data-test-id={'nextLineSqft'}
                className={'spanBeforeSeparator'}
              >
                {sqft} sqft
              </_BeforeSeparator>
            ) : null
          }
          {
            (!sqft && hasSqftRange) ? (
              <_BeforeSeparator
                data-test-id={'sqftRange'}
                className={'spanBeforeSeparator'}
              >
                {sqftRange}
              </_BeforeSeparator>
            ) : null
          }
          {
            sqft && showPsf ? <span>${psf}/sqft</span> : ''
          }
          {
            listing.bed_type && hasMaint && (
              <_MaintFee
                data-test-id={'maintFee'}
                className={'spanBeforeSeparator'}
              >
                Maint. Fee {formatPrice(Number(listing.maintenance_fee))}
              </_MaintFee>
            )
          }
          {
            isBc && (
              <_BrokerageInfo>
                <div>MLS#: {listing.mls_number}</div>
                {listing.brokerage && (
                  <>
                    <_Separator> |</_Separator>
                    <EllipsisText>{listing.brokerage}</EllipsisText>
                  </>
                )
                }
              </_BrokerageInfo>
            )
          }
        </_InfoContainer>

      </div>

    </_ListingSpecUl>
  )
}

ListingSpec.propTypes = {
  listing: PropTypes.shape({
    bed_type: PropTypes.string,
    bathrooms: PropTypes.number,
    sqft_max: PropTypes.number,
    sqft_min: PropTypes.number,
    parking_spots: PropTypes.number,
    sqft: PropTypes.number,
    closed_price: PropTypes.number,
    asking_price: PropTypes.number,
    maintenance_fee: PropTypes.number,
    property_type: PropTypes.string,
    sqft_source: PropTypes.string,
    brokerage: PropTypes.string,
    mls_number: PropTypes.string,
  }),
  offer: PropTypes.string,
  showPsf: PropTypes.bool,
  className: PropTypes.string,
  smallerThan320: PropTypes.bool,
  smallerThan375: PropTypes.bool,
  userIsAuthentificated: PropTypes.bool,
  isBc: PropTypes.bool,
}

export default memo(ListingSpec)
